/**
 * What kind of question a line asks, in one place.
 *
 * The spec calls for "one registry of task-type handlers, not if/else scattered
 * through the codebase", and the reason is not tidiness. Scattered conditionals
 * drift: one of them gets updated and the other four do not, and the result is a
 * screen that offers GOOD/BAD/UNSURE for a crate of pork. The database now
 * refuses to store that, but a button that cannot work is still a defect — the
 * operator taps it and nothing happens, which is worse than the button being
 * absent.
 *
 * So every place that behaves differently per task reads this table instead of
 * testing a string. Backend and frontend both import it, which is what keeps the
 * API's rules and the UI's affordances from disagreeing.
 *
 * ADDING A TASK TYPE means adding an entry here and satisfying its contract. It
 * does not mean hunting for conditionals.
 */
export const TASKS = {
    anomaly: {
        id: 'anomaly',
        label: 'Quality inspection',
        summary: 'Is this part like the good ones?',
        question: 'Is this part acceptable?',
        usesVerdict: true,
        usesClasses: false,
        usesRegions: true,
        usesDefectTypes: true,
        outputTypes: ['anomaly_score'],
        backend: 'onnx',
        hiddenRoutes: ['/classes', '/classify', '/confusion'],
        vocabulary: { result: 'verdict', correction: 'relabel', taxonomy: 'defect types' },
    },
    classification: {
        id: 'classification',
        label: 'Classification',
        summary: 'Which of these products is it?',
        question: 'Which product is this?',
        usesVerdict: false,
        usesClasses: true,
        // Crate-level classification labels the whole image. Regions arrive with
        // detection (variant 3); the column exists so that is a config change rather
        // than a migration, but nothing offers them yet.
        usesRegions: false,
        usesDefectTypes: false,
        outputTypes: ['logits', 'probabilities', 'embedding', 'multilabel', 'ordinal'],
        backend: 'classifier',
        hiddenRoutes: ['/labels', '/annotate'],
        vocabulary: { result: 'prediction', correction: 'correction', taxonomy: 'classes' },
    },
};
export const TASK_LIST = Object.values(TASKS);
export function taskOf(task) {
    // Unknown or missing falls back to anomaly, which is what every product was
    // before this existed. Throwing here would take down a page over a typo in a
    // column; the guards below are where wrongness is caught.
    return TASKS[task ?? 'anomaly'] ?? TASKS.anomaly;
}
/**
 * Why a write does not fit its task, or null when it does.
 *
 * The database enforces the same rules and is the real boundary. This exists so
 * a caller gets a sentence naming what is wrong instead of a constraint name —
 * "a quality verdict on a classification product" rather than
 * `inspections_shape_matches_task`. Both are needed: the message is for people,
 * the constraint is for correctness.
 */
export function inspectionFits(task, shape) {
    const def = TASKS[task];
    if (!def)
        return `Unknown task type "${task}"`;
    if (!def.usesVerdict && shape.verdict != null) {
        return (`A ${def.label.toLowerCase()} product records ${def.vocabulary.result}s, not a ` +
            `quality verdict. "${shape.verdict}" says whether something is acceptable; this line ` +
            `asks ${def.question.toLowerCase()}`);
    }
    if (def.usesVerdict && shape.verdict == null) {
        return `A ${def.label.toLowerCase()} inspection needs a verdict (GOOD, BAD or UNSURE)`;
    }
    if (!def.usesClasses && shape.classId != null) {
        return `A ${def.label.toLowerCase()} product has no classes to assign`;
    }
    if (def.usesClasses && shape.classId == null && shape.decision !== 'reject') {
        return ('A classification must name a class, or record decision="reject" when the model ' +
            'was not confident enough to name one');
    }
    return null;
}
export function annotationFits(task, shape) {
    const def = TASKS[task];
    if (!def)
        return `Unknown task type "${task}"`;
    if (!def.usesVerdict && shape.label != null) {
        return `A ${def.label.toLowerCase()} annotation records a class, not GOOD/BAD/UNSURE`;
    }
    if (def.usesVerdict && shape.label == null) {
        return 'A quality annotation needs a label (GOOD, BAD or UNSURE)';
    }
    if (!def.usesClasses && shape.classId != null) {
        return `A ${def.label.toLowerCase()} product has no classes to assign`;
    }
    if (def.usesClasses && shape.classId == null) {
        return 'A classification annotation must name a class';
    }
    if (!def.usesRegions && shape.regions && shape.regions.length > 0) {
        return (`${def.label} labels the whole image; drawing regions belongs to per-object ` +
            'detection, which this task does not do');
    }
    return null;
}
/**
 * Whether a model may be activated for a product.
 *
 * The check that stops the quiet disaster: an anomaly score near 1 means "unlike
 * anything I know" and a classification confidence near 1 means "certainly this
 * class". Same range, opposite meaning, and one of them ends up wired to a
 * rejector.
 */
export function modelFitsProduct(productTask, modelTask, outputType) {
    if (productTask !== modelTask) {
        return (`This is a ${TASKS[modelTask]?.label.toLowerCase() ?? modelTask} model and the product ` +
            `does ${TASKS[productTask]?.label.toLowerCase() ?? productTask}. Their scores look alike ` +
            'and mean opposite things.');
    }
    if (outputType && !TASKS[productTask].outputTypes.includes(outputType)) {
        return (`A ${TASKS[productTask].label.toLowerCase()} model cannot emit "${outputType}". ` +
            `Expected one of: ${TASKS[productTask].outputTypes.join(', ')}`);
    }
    return null;
}
//# sourceMappingURL=tasks.js.map